import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageProps } from "../types/auth";

import styles from './styles/SearchPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface SearchResult {
  id: number;
  display: string;
}

interface SearchResultGroup {
  result_type: "users" | "groups" | "curriculums";
  results: SearchResult[];
}

const SearchPage: React.FC<PageProps> = ({ token, selectedOrgID }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState<string>(searchParams.get("search_term") || "");
  const [results, setResults] = useState<SearchResultGroup[]>([]);
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();

  // Update searchTerm when URL parameters change
  useEffect(() => {
    const urlSearchTerm = searchParams.get("search_term") || "";
    setSearchTerm(urlSearchTerm);
    if (urlSearchTerm) {
      performSearch(urlSearchTerm);
    }
  }, [searchParams]); // Add searchParams as a dependency

  const performSearch = async (term: string) => {
    if (!term.trim()) {
      setResults([]);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/search?search_term=${encodeURIComponent(term)}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to perform search.");
        return;
      }

      const data: SearchResultGroup[] = await response.json();
      setResults(data);
    } catch (err) {
      setError("An unexpected error occurred.");
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(searchTerm ? { search_term: searchTerm } : {});
    performSearch(searchTerm);
  };

  const getNavigationPath = (type: string, id: number) => {
    switch (type) {
      case "users":
        return `/members/${id}`;
      case "groups":
        return `/groups/${id}`;
      case "curriculums":
        return `/curriculums/${id}`;
      default:
        return "#";
    }
  };

  return (
    <div className={styles.container}>
      <h1>Search</h1>

      <form onSubmit={handleSearch} className={styles.searchForm}>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Enter search term..."
          className={styles.searchInput}
        />
        <button type="submit" className={styles.searchButton}>Search</button>
      </form>

      {error && <p className={styles.errorMessage}>{error}</p>}

      <div className={styles.searchResults}>
        {results.map((group) => (
          <div key={group.result_type} className={styles.resultGroup}>
            <h3>{group.result_type.charAt(0).toUpperCase() + group.result_type.slice(1)}</h3>
            {group.results.length > 0 ? (
              <ul className={styles.resultsList}>
                {group.results.map((result) => (
                  <li key={result.id} className={styles.resultItem}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(getNavigationPath(group.result_type, result.id));
                      }}
                      className={styles.resultLink}
                    >
                      {result.display}
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <p className={styles.noResults}>No {group.result_type} found.</p>
            )}
          </div>
        ))}
        {searchTerm && results.length === 0 && (
          <p className={styles.noResults}>No results found.</p>
        )}
      </div>
    </div>
  );
};

export default SearchPage; 